import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  LayoutDashboard, Phone, Users, BarChart3, FileDown,
  ChevronLeft, ChevronRight, LogOut, Activity, Shield, Menu, X, Settings, Cpu, ScrollText, CheckSquare, Tags, TrendingUp, GraduationCap
} from 'lucide-react';
import { Button } from '../components/ui/button';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'supervisor', 'agent'] },
  { to: '/calls', icon: Phone, label: 'Calls', roles: ['admin', 'supervisor', 'agent'] },
  // { to: '/processed', icon: CheckSquare, label: 'Processed Calls', roles: ['admin', 'supervisor'] },
  { to: '/agents', icon: BarChart3, label: 'Agent Analytics', roles: ['admin', 'supervisor'] },
  // Coaching screen: which scored parameters this person is weak in, and how to
  // fix each one. Agents and supervisors both see their OWN calls here — team
  // views live on Agent Analytics — so the label is the same for both.
  { to: '/my-coaching', icon: GraduationCap, label: 'My Coaching', roles: ['agent', 'supervisor'] },
  // { to: '/processing', icon: Cpu, label: 'Processing', roles: ['admin', 'supervisor'] },
  { to: '/gemini-processing', icon: Cpu, label: 'Auto Processing', roles: ['admin'] },
  { to: '/tags-dashboard', icon: TrendingUp, label: 'Tags Dashboard', roles: ['admin', 'supervisor'] },
  { to: '/tags', icon: Tags, label: 'Tags', roles: ['admin', 'supervisor'] },
  // { to: '/fetch-logs', icon: ScrollText, label: 'Fetch Logs', roles: ['admin'] },
  { to: '/users', icon: Users, label: 'User Management', roles: ['admin'] },
  { to: '/reports', icon: FileDown, label: 'Reports', roles: ['admin', 'supervisor'] },
  { to: '/settings', icon: Settings, label: 'Settings', roles: ['admin'] },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNav = navItems.filter(item => item.roles.includes(user?.role));

  const roleBadge = user?.role === 'admin' ? 'role-admin' : user?.role === 'supervisor' ? 'role-supervisor' : 'role-agent';

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar-gradient border-r border-border flex flex-col z-50
        ${collapsed ? 'w-16' : 'w-60'}
        fixed lg:relative h-full
        transition-[width,transform] duration-200
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Activity className="w-4 h-4 text-primary" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <h1 className="text-sm font-semibold text-foreground truncate">Voice Care</h1>
              <p className="text-[10px] text-muted-foreground">Monitor</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {filteredNav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-150
                ${isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:text-foreground hover:bg-white/5'}
                ${collapsed ? 'justify-center' : ''}`
              }
              data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}-nav-link`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        <div className="border-t border-border p-3">
          {!collapsed && (
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Shield className="w-3.5 h-3.5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{user?.name}</p>
                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${roleBadge}`}>
                  {user?.role}
                </span>
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            className={`w-full text-muted-foreground hover:text-foreground ${collapsed ? 'justify-center px-0' : 'justify-start'}`}
            onClick={handleLogout}
            data-testid="sidebar-logout-button"
          >
            <LogOut className="w-4 h-4" />
            {!collapsed && <span className="ml-2">Logout</span>}
          </Button>
        </div>

        {/* Collapse button (desktop) */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute -right-3 top-8 w-6 h-6 rounded-full bg-card border border-border items-center justify-center text-muted-foreground hover:text-foreground"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-md border-b border-border px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4">
          <button
            className="lg:hidden p-1.5 rounded-md hover:bg-white/5"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:block">{user?.email}</span>
          </div>
        </header>

        {/* Page content */}
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
